import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { getWeightLogs, getWorkoutLogs, getMedications } from '../../services/firestoreService';
import { format } from 'date-fns';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';

export default function Dashboard() {
  const { theme } = useTheme();
  const { user, userProfile } = useAuth();
  const [weightLogs, setWeightLogs] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [medications, setMedications] = useState([]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getWeightLogs(user.uid, 14),
      getWorkoutLogs(user.uid, 7),
      getMedications(user.uid),
    ]).then(([w, wo, m]) => {
      setWeightLogs(w.reverse());
      setWorkoutLogs(wo);
      setMedications(m);
    });
  }, [user]);

  const currentWeight = weightLogs[weightLogs.length - 1]?.weight || userProfile?.currentWeight || '--';
  const goalWeight = userProfile?.goals?.targetWeight || '--';
  const weightToGo = currentWeight !== '--' && goalWeight !== '--'
    ? Math.abs(currentWeight - goalWeight).toFixed(1) : '--';

  const progressPct = currentWeight !== '--' && goalWeight !== '--' && userProfile?.currentWeight
    ? Math.min(100, Math.round(
        ((userProfile.currentWeight - currentWeight) / (userProfile.currentWeight - goalWeight)) * 100
      )) : 0;

  const statCard = (icon, label, value, sub, color) => (
    <div className="card" style={{ flex: 1, minWidth: '160px' }}>
      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{icon}</div>
      <div style={{ color: theme.textSecondary, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: color || theme.text }}>{value}</div>
      {sub && <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px' }} className="animate-fade">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text }}>
          Welcome back, {user?.displayName?.split(' ')[0] || 'Athlete'} ⚡
        </h1>
        <p style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      {/* Stat Row */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {statCard('⚖️', 'Current Weight', currentWeight !== '--' ? `${currentWeight} lbs` : '--', 'Last logged', theme.accent)}
        {statCard('🎯', 'Goal Weight', goalWeight !== '--' ? `${goalWeight} lbs` : '--', 'Target', theme.success)}
        {statCard('📉', 'To Goal', weightToGo !== '--' ? `${weightToGo} lbs` : '--', 'Remaining', theme.warning)}
        {statCard('💪', 'Workouts', workoutLogs.length, 'This week', theme.accentSecondary)}
        {statCard('💊', 'Medications', medications.length, 'Active', theme.danger)}
      </div>

      {/* Progress Bar */}
      {progressPct > 0 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text }}>
              Weight Goal Progress
            </span>
            <span style={{ color: theme.accent, fontWeight: 700 }}>{progressPct}%</span>
          </div>
          <div style={{ height: '12px', background: theme.border, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${theme.accent}, ${theme.success})`,
              borderRadius: '6px', transition: 'width 1s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.75rem', color: theme.textSecondary }}>
            <span>Start: {userProfile?.currentWeight} lbs</span>
            <span>Goal: {goalWeight} lbs</span>
          </div>
        </div>
      )}

      {/* Weight Chart */}
      {weightLogs.length > 1 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '1rem', color: theme.text }}>
            📈 Weight Trend (Last 2 Weeks)
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={weightLogs}>
              <Tooltip
                contentStyle={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text }}
                formatter={(v) => [`${v} lbs`, 'Weight']}
              />
              <Line type="monotone" dataKey="weight" stroke={theme.accent} strokeWidth={2} dot={{ fill: theme.accent, r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <Link to="/muscles" className="card" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '1rem', marginBottom: '2rem', textDecoration: 'none',
        background: `linear-gradient(135deg, ${theme.bgCard}, ${theme.bgSecondary})`,
        borderColor: theme.accent,
      }}>
        <div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1.15rem', color: theme.text }}>
            🧍 Muscle Explorer
          </div>
          <div style={{ color: theme.textSecondary, fontSize: '0.85rem', marginTop: '0.25rem' }}>
            Click a muscle group to learn, pick exercises, and add them to a workout.
          </div>
        </div>
        <span style={{ color: theme.accent, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, whiteSpace: 'nowrap' }}>
          Open →
        </span>
      </Link>

      {/* Recent Workouts */}
      {workoutLogs.length > 0 && (
        <div className="card">
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '1rem', color: theme.text }}>
            💪 Recent Workouts
          </h3>
          {workoutLogs.slice(0, 3).map(log => (
            <div key={log.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}`,
            }}>
              <div>
                <div style={{ fontWeight: 600, color: theme.text }}>{log.name}</div>
                <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>{log.exercises?.length || 0} exercises</div>
              </div>
              <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>
                {log.loggedAt?.toDate ? format(log.loggedAt.toDate(), 'MMM d') : 'Recent'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
